import React, { useEffect, useMemo, useState, useCallback } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";

let stripePromiseCache = null;
export async function getStripe() {
  if (!stripePromiseCache) {
    const { data } = await api.get("/payments/config");
    stripePromiseCache = loadStripe(data.publishable_key);
  }
  return stripePromiseCache;
}

export function StripeCheckoutSheet({ open, clientSecret, sessionId, onClose, onPaid }) {
  const [stripe, setStripe] = useState(null);
  const [status, setStatus] = useState("checkout"); // checkout | confirming | paid

  useEffect(() => {
    if (open) { getStripe().then(setStripe); setStatus("checkout"); }
  }, [open]);

  const pollPaid = useCallback(async () => {
    setStatus("confirming");
    let tries = 0;
    const tick = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setStatus("paid"); setTimeout(() => onPaid && onPaid(), 1200); return; }
      } catch {}
      tries += 1;
      if (tries < 10) setTimeout(tick, 1500);
      else { onPaid && onPaid(); }
    };
    tick();
  }, [sessionId, onPaid]);

  const options = useMemo(() => ({ clientSecret, onComplete: pollPaid }), [clientSecret, pollPaid]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div className="fixed inset-0 z-[80]" style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)" }}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} data-testid="checkout-backdrop" />
          <motion.div
            className="fixed left-0 right-0 bottom-0 z-[90] mx-auto rounded-t-3xl flex flex-col"
            style={{ maxWidth: 480, height: "94dvh", background: "var(--bg-surface)" }}
            initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            data-testid="checkout-sheet">
            <div className="flex items-center justify-between px-5 pt-4 pb-3 shrink-0" style={{ borderBottom: "1px solid var(--hairline)" }}>
              <h3 className="font-serif-display text-2xl" style={{ color: "var(--text-primary)" }}>Checkout</h3>
              <button onClick={onClose} data-testid="checkout-close-btn" className="p-2 rounded-full"><X size={20} /></button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-2 py-2" style={{ background: "#fff" }}>
              {status === "paid" ? (
                <div className="grid place-items-center h-full text-center px-6" style={{ background: "#fff" }}>
                  <div>
                    <CheckCircle2 size={56} className="mx-auto" style={{ color: "#5B63E8" }} data-testid="checkout-paid" />
                    <div className="font-serif-display text-3xl mt-4" style={{ color: "#171826" }}>You're all set!</div>
                    <p className="text-sm mt-2" style={{ color: "#565A6E" }}>Your RSVP is confirmed and paid.</p>
                  </div>
                </div>
              ) : status === "confirming" ? (
                <div className="grid place-items-center h-full" style={{ background: "#fff" }}>
                  <div className="text-center">
                    <Loader2 size={40} className="animate-spin mx-auto" style={{ color: "#5B63E8" }} />
                    <div className="mt-3 text-sm" style={{ color: "#565A6E" }}>Confirming your payment…</div>
                  </div>
                </div>
              ) : stripe && clientSecret ? (
                <EmbeddedCheckoutProvider stripe={stripe} options={options}>
                  <EmbeddedCheckout />
                </EmbeddedCheckoutProvider>
              ) : (
                <div className="grid place-items-center h-full" style={{ background: "#fff" }}>
                  <Loader2 size={36} className="animate-spin" style={{ color: "#5B63E8" }} />
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
